import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  House, Users, GraduationCap, BookOpen, Calendar, ChartBar,
  CurrencyInr, Megaphone, Video, FileText, UserCircle, Gear,
  SignOut, CaretLeft, CaretRight, MagnifyingGlass, Bell, CaretDown,
  ChatCircle, Clock, Target, TrendingUp, Student, Clipboard, Lock, Warning,
  Exam, Certificate
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const menuItems = [
  { 
    section: 'Main',
    items: [
      { icon: House, label: 'Dashboard', path: '/center/dashboard', id: 'dashboard' },
    ]
  },
  {
    section: 'Management',
    items: [
      { icon: Users, label: 'Students', path: '/center/students', id: 'students' },
      { icon: GraduationCap, label: 'Courses', path: '/center/courses', id: 'courses' },
      { icon: Calendar, label: 'Batches', path: '/center/batches', id: 'batches' },
      { icon: Clipboard, label: 'Attendance', path: '/center/attendance', id: 'attendance' },
    ]
  },
  {
    section: 'Business',
    items: [
      { icon: CurrencyInr, label: 'Finance', path: '/center/finance', id: 'finance' },
      { icon: Megaphone, label: 'CRM / Leads', path: '/center/crm', id: 'crm' },
    ]
  },
  {
    section: 'Learning',
    items: [
      { icon: Video, label: 'Live Classes', path: '/center/lms', id: 'lms' },
      { icon: Exam, label: 'Exams', path: '/center/exams', id: 'exams' },
      { icon: Certificate, label: 'Certificates', path: '/center/certificates', id: 'certificates' },
      { icon: FileText, label: 'Reports', path: '/center/reports', id: 'reports' },
    ]
  },
  {
    section: 'System',
    items: [
      { icon: UserCircle, label: 'Staff', path: '/center/staff', id: 'staff' },
      { icon: Gear, label: 'Settings', path: '/center/settings', id: 'settings' },
    ]
  }
];

// Center Status Constants (must match backend)
const CENTER_STATUS = {
  UNDER_REVIEW: 'UNDER_REVIEW',
  APPROVED_PENDING_PAYMENT: 'APPROVED_PENDING_PAYMENT',
  ACTIVE: 'ACTIVE',
  REJECTED: 'REJECTED'
};

const CenterLayout = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [centerInfo, setCenterInfo] = useState(null);
  const [pendingAlerts, setPendingAlerts] = useState(0);
  const [centerStatus, setCenterStatus] = useState(null);
  
  // Refs for click outside detection
  const profileDropdownRef = useRef(null);
  const notificationDropdownRef = useRef(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  
  const sidebarWidth = sidebarCollapsed ? 80 : 260;
  
  // Check if center is active (only ACTIVE status allows full access)
  const isCenterActive = centerStatus === CENTER_STATUS.ACTIVE;

  // Close dropdowns on route change
  useEffect(() => {
    setShowProfileMenu(false);
    setShowNotifications(false);
  }, [location.pathname]);

  // Click outside handler for profile dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
      if (notificationDropdownRef.current && !notificationDropdownRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setShowProfileMenu(false);
        setShowNotifications(false);
      }
    };

    if (showProfileMenu || showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showProfileMenu, showNotifications]);

  useEffect(() => {
    // Check if center is logged in
    const token = localStorage.getItem('center_token');
    if (!token) {
      navigate('/auth/login');
      return;
    }
    fetchCenterInfo();
    fetchNotifications();
  }, [navigate]);

  const fetchCenterInfo = async () => {
    try {
      const token = localStorage.getItem('center_token');
      if (!token) {
        navigate('/auth/login');
        return;
      }
      
      const response = await axios.get(`${API}/center/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCenterInfo(response.data);
      
      // Set center status from response
      const status = response.data?.status?.toUpperCase() || '';
      if (status === 'ACTIVE' || response.data?.is_active === true || response.data?.dashboard_unlocked === true) {
        setCenterStatus(CENTER_STATUS.ACTIVE);
      } else if (status === 'APPROVED_PENDING_PAYMENT' || status === 'STAGE1_APPROVED' || response.data?.show_payment === true) {
        setCenterStatus(CENTER_STATUS.APPROVED_PENDING_PAYMENT);
      } else if (status === 'REJECTED') {
        setCenterStatus(CENTER_STATUS.REJECTED);
      } else {
        setCenterStatus(CENTER_STATUS.UNDER_REVIEW);
      }
    } catch (error) {
      console.error('Failed to fetch center info:', error);
      // If token is invalid, redirect to login
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('center_token');
        localStorage.removeItem('center_info');
        navigate('/auth/login');
      }
    }
  };

  const fetchNotifications = async () => {
    // Mock notifications
    setNotifications([
      { id: 1, title: 'Fee pending: 5 students', time: '10 min ago', unread: true, type: 'warning' },
      { id: 2, title: 'New lead assigned', time: '1 hour ago', unread: true, type: 'info' },
      { id: 3, title: 'Batch starts tomorrow', time: '2 hours ago', unread: false, type: 'reminder' },
    ]);
    setPendingAlerts(3);
  };

  const handleLogout = () => {
    localStorage.removeItem('center_token');
    localStorage.removeItem('center_info');
    navigate('/auth/login');
  };

  const isActivePath = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const unreadCount = notifications.filter(n => n.unread).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{ width: sidebarWidth }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="fixed left-0 top-0 h-screen bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800 border-r border-slate-700/50 flex flex-col z-50 shadow-2xl"
        data-testid="center-sidebar"
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-4 border-b border-slate-700/50">
          <Link to="/center/dashboard" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
              <img src="/iqraskills-logo.png" alt="IqraSkills" className="w-full h-full object-contain" />
            </div>
            <AnimatePresence>
              {!sidebarCollapsed && (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className="flex flex-col"
                >
                  <span className="text-lg font-bold text-white">Iqra Skills</span>
                  <span className="text-[10px] text-purple-400 font-medium">Training Center</span>
                </motion.div>
              )}
            </AnimatePresence>
          </Link>
        </div>

        {/* Center Code Badge */}
        <AnimatePresence>
          {!sidebarCollapsed && centerInfo && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="px-4 py-3 border-b border-slate-700/50"
            >
              <div className="bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-lg px-3 py-2 border border-purple-500/30">
                <p className="text-[10px] text-slate-400 uppercase tracking-wider">Center Code</p>
                <p className="text-sm font-mono text-purple-300 font-semibold">{centerInfo.center_code || 'IQRA-XX-XXXXX'}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 scrollbar-thin scrollbar-thumb-slate-700">
          {menuItems.map((section, idx) => (
            <div key={idx} className="mb-5">
              <AnimatePresence>
                {!sidebarCollapsed && (
                  <motion.h3
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 px-3 mb-2"
                  >
                    {section.section}
                  </motion.h3>
                )}
              </AnimatePresence>
              <div className="space-y-1">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = isActivePath(item.path);
                  // Dashboard is always accessible, other items are locked if center not ACTIVE
                  const isLocked = !isCenterActive && item.id !== 'dashboard';
                  
                  // Locked item styling
                  if (isLocked) {
                    return (
                      <div
                        key={item.id}
                        data-testid={`sidebar-${item.id}-locked`}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 text-slate-600 cursor-not-allowed opacity-50"
                        title="Activate your center to unlock"
                      >
                        <div className="relative">
                          <Icon weight="regular" className="w-5 h-5 flex-shrink-0" />
                          <Lock weight="fill" className="w-3 h-3 absolute -bottom-1 -right-1 text-amber-500" />
                        </div>
                        <AnimatePresence>
                          {!sidebarCollapsed && (
                            <motion.span
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              className="text-sm font-medium truncate"
                            >
                              {item.label}
                            </motion.span>
                          )}
                        </AnimatePresence>
                        {!sidebarCollapsed && (
                          <Lock weight="fill" className="ml-auto w-3.5 h-3.5 text-amber-500" />
                        )}
                      </div>
                    );
                  }
                  
                  return (
                    <Link
                      key={item.id}
                      to={item.path}
                      data-testid={`sidebar-${item.id}`}
                      className={`
                        w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200
                        ${isActive 
                          ? 'bg-gradient-to-r from-purple-600/30 to-blue-600/20 text-white border border-purple-500/30 shadow-lg shadow-purple-500/10' 
                          : 'text-slate-400 hover:bg-white/5 hover:text-white'
                        }
                      `}
                    >
                      <Icon 
                        weight={isActive ? 'fill' : 'regular'} 
                        className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-purple-400' : ''}`} 
                      />
                      <AnimatePresence>
                        {!sidebarCollapsed && (
                          <motion.span
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="text-sm font-medium truncate"
                          >
                            {item.label}
                          </motion.span>
                        )}
                      </AnimatePresence>
                      {isActive && !sidebarCollapsed && (
                        <motion.div
                          layoutId="centerSidebarIndicator"
                          className="ml-auto w-1.5 h-1.5 rounded-full bg-purple-400"
                        />
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Collapse Toggle */}
        <div className="p-3 border-t border-slate-700/50">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-slate-400 hover:bg-white/5 hover:text-white transition-all"
            data-testid="sidebar-toggle"
          >
            {sidebarCollapsed ? (
              <CaretRight className="w-5 h-5" />
            ) : (
              <>
                <CaretLeft className="w-5 h-5" />
                <span className="text-sm font-medium">Collapse</span>
              </>
            )}
          </button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <div 
        className="transition-all duration-300"
        style={{ marginLeft: sidebarWidth }}
      >
        {/* Topbar */}
        <header
          className="sticky top-0 h-16 bg-white/95 backdrop-blur-xl border-b border-slate-200/80 z-40 shadow-sm"
          data-testid="center-topbar"
        >
          <div className="h-full flex items-center justify-between px-6">
            {/* Left: Center Code Badge */}
            <div className="flex items-center gap-4">
              <div className="hidden md:flex items-center gap-2 bg-gradient-to-r from-purple-50 to-blue-50 px-4 py-2 rounded-xl border border-purple-200">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-sm font-semibold text-purple-700">
                  {centerInfo?.center_code || 'Loading...'}
                </span>
              </div>
            </div>

            {/* Center: Search */}
            <div className="flex-1 max-w-lg mx-4">
              <div className="relative">
                <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search students, courses, leads..."
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-100 border-0 rounded-xl text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:bg-white transition-all"
                  data-testid="topbar-search"
                />
              </div>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              {/* Messages */}
              <button className="p-2.5 rounded-xl text-slate-600 hover:bg-slate-100 transition-colors relative">
                <ChatCircle className="w-5 h-5" />
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-blue-500 rounded-full" />
              </button>

              {/* Notifications */}
              <div className="relative" ref={notificationDropdownRef}>
                <button
                  onClick={() => {
                    setShowNotifications(!showNotifications);
                    setShowProfileMenu(false);
                  }}
                  className={`p-2.5 rounded-xl text-slate-600 transition-all duration-200 relative ${
                    showNotifications ? 'bg-slate-100 ring-2 ring-purple-500/20' : 'hover:bg-slate-100'
                  }`}
                  data-testid="topbar-notifications"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </button>

                <AnimatePresence>
                  {showNotifications && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.15, ease: 'easeOut' }}
                      className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-50"
                    >
                      <div className="p-4 border-b border-slate-100 bg-gradient-to-r from-purple-50 to-blue-50">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-slate-900">Notifications</h3>
                          {unreadCount > 0 && (
                            <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded-full font-medium">
                              {unreadCount} new
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="max-h-80 overflow-y-auto">
                        {notifications.map((notif) => (
                          <div
                            key={notif.id}
                            className={`p-4 border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer ${
                              notif.unread ? 'bg-purple-50/50' : ''
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-2 h-2 rounded-full mt-2 ${
                                notif.type === 'warning' ? 'bg-amber-500' :
                                notif.type === 'info' ? 'bg-blue-500' : 'bg-purple-500'
                              }`} />
                              <div>
                                <p className="text-sm font-medium text-slate-900">{notif.title}</p>
                                <p className="text-xs text-slate-500 mt-0.5">{notif.time}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Profile Dropdown */}
              <div className="relative" ref={profileDropdownRef}>
                <button
                  onClick={() => {
                    setShowProfileMenu(!showProfileMenu);
                    setShowNotifications(false);
                  }}
                  className={`flex items-center gap-3 p-2 rounded-xl transition-all duration-200 ${
                    showProfileMenu ? 'bg-slate-100 ring-2 ring-purple-500/20' : 'hover:bg-slate-100'
                  }`}
                  data-testid="topbar-profile"
                >
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-600 to-blue-500 flex items-center justify-center shadow-lg shadow-purple-500/20">
                    <span className="text-white font-semibold text-sm">
                      {centerInfo?.owner_name?.charAt(0) || 'C'}
                    </span>
                  </div>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-slate-900">
                      {centerInfo?.center_name || 'Training Center'}
                    </p>
                    <p className="text-xs text-slate-500">
                      {centerInfo?.owner_name || 'Owner'}
                    </p>
                  </div>
                  <CaretDown className={`w-4 h-4 text-slate-400 hidden md:block transition-transform duration-200 ${showProfileMenu ? 'rotate-180' : ''}`} />
                </button>

                <AnimatePresence>
                  {showProfileMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.15, ease: 'easeOut' }}
                      className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-50"
                    >
                      <div className="p-4 border-b border-slate-100 bg-gradient-to-r from-purple-50 to-blue-50">
                        <p className="font-medium text-slate-900">{centerInfo?.center_name}</p>
                        <p className="text-xs text-slate-500 font-mono mt-1">{centerInfo?.center_code}</p>
                      </div>
                      <div className="p-2">
                        <Link
                          to="/center/settings"
                          onClick={() => setShowProfileMenu(false)}
                          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors"
                        >
                          <Gear className="w-4 h-4" />
                          <span className="text-sm">Settings</span>
                        </Link>
                      </div>
                      <div className="p-2 border-t border-slate-100">
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                          data-testid="topbar-logout"
                        >
                          <SignOut className="w-4 h-4" />
                          <span className="text-sm">Logout</span>
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default CenterLayout;
